import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { Loader2, ArrowLeft, AlertTriangle } from "lucide-react";

const STATUSES = ["open", "in_review", "resolved", "closed"];
const statusColor = (s) => ({
  open: "bg-amber-100 text-amber-800",
  in_review: "bg-blue-100 text-blue-800",
  resolved: "bg-emerald-100 text-emerald-800",
  closed: "bg-gray-200 text-gray-700",
}[s] || "bg-gray-200 text-gray-700");

const catLabel = { beneficiary: "Beneficiary", vendor: "Vendor", sponsor_programme: "Sponsor / Programme" };

export default function ComplaintsAdmin() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState({});

  const load = () => api.get("/admin/complaints").then((r) => setRows(r.data.complaints)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const setD = (t, k, v) => setDraft((p) => ({ ...p, [t]: { ...p[t], [k]: v } }));

  const update = async (c) => {
    const d = draft[c.ticket] || {};
    const status = d.status || c.status;
    try {
      await api.patch(`/admin/complaints/${c.ticket}`, { status, note: d.note || "" });
      toast.success(`${c.ticket} → ${status.replace("_", " ")}`);
      setDraft((p) => ({ ...p, [c.ticket]: { note: "" } }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <button onClick={() => navigate("/admin")} className="text-emerald-700 flex items-center gap-2 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back to Command Centre</button>
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Dispute Resolution</span>
            <h1 className="font-display font-bold text-3xl text-emerald-950">Complaints Management</h1>
          </div>
        </div>

        {!rows ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
        ) : rows.length === 0 ? (
          <div className="mt-8 text-center text-gray-500 py-16 border border-dashed border-emerald-200 rounded-2xl">No complaints yet. Tickets raised on the public Report a Problem page will appear here.</div>
        ) : (
          <div className="mt-8 space-y-4">
            {rows.map((c) => (
              <div key={c.ticket} data-testid={`admin-complaint-${c.ticket}`} className="rounded-2xl border border-emerald-100 bg-white p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-emerald-700">{c.ticket}</span>
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-100">{catLabel[c.category] || c.category}</Badge>
                    <Badge className={statusColor(c.status)}>{String(c.status).replace("_", " ")}</Badge>
                  </div>
                  <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <h3 className="mt-2 font-semibold text-emerald-950">{c.subject}</h3>
                <p className="mt-1 text-sm text-gray-600">{c.message}</p>
                <div className="mt-2 text-xs text-gray-500">{c.name}{c.phone ? ` · ${c.phone}` : ""}{c.email ? ` · ${c.email}` : ""}</div>

                <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
                  <Select value={(draft[c.ticket]?.status) || c.status} onValueChange={(v) => setD(c.ticket, "status", v)}>
                    <SelectTrigger data-testid={`admin-complaint-status-${c.ticket}`} className="h-10 w-full sm:w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid={`admin-complaint-note-${c.ticket}`} value={draft[c.ticket]?.note || ""} onChange={(e) => setD(c.ticket, "note", e.target.value)} placeholder="Add a note (optional)" className="h-10 flex-1" />
                  <Button data-testid={`admin-complaint-update-${c.ticket}`} onClick={() => update(c)} className="h-10 bg-emerald-600 hover:bg-emerald-700">Update</Button>
                </div>

                <div className="mt-3 space-y-1">
                  {(c.history || []).map((h, i) => (
                    <div key={`${h.at}-${i}`} className="text-xs text-gray-500 flex justify-between gap-3 border-t border-emerald-50 pt-1.5">
                      <span className="capitalize">{String(h.status).replace("_", " ")}{h.note ? ` — ${h.note}` : ""}</span>
                      <span className="text-gray-400 whitespace-nowrap">{new Date(h.at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
